import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Gift, Star, Sparkles, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function RatgeberTraditionPage() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]" data-testid="ratgeber-tradition-page">
      {/* Hero */}
      <section className="py-12 md:py-20 bg-[#F5F5F0]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/ratgeber" className="inline-flex items-center gap-2 text-[#4A5D4F] hover:text-[#3A493E] mb-6">
            <ArrowLeft className="w-4 h-4" />
            Alle Ratgeber
          </Link>
          <div className="flex items-center gap-2 text-sm text-[#6B7280] mb-4">
            <Calendar className="w-4 h-4" />
            <span>Aktualisiert: Februar 2026</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-[#2C332E] mb-6 leading-tight">
            Eid Mubarak Geschenke: Tradition trifft Moderne
          </h1>
          <p className="text-lg text-[#6B7280] leading-relaxed">
            Die Kunst des Schenkens zu Eid verbindet jahrhundertealte Traditionen mit 
            modernen Geschenkideen. Erfahre, wie du das perfekte Eid Mubarak Geschenk 
            findest, das Wertschätzung ausdrückt und Freude bereitet.
          </p>
        </div>
      </section>

      {/* Content */}
      <article className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="prose prose-lg max-w-none">
            
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mt-8 mb-6 flex items-center gap-3">
              <Star className="w-6 h-6 text-[#D4C5A9]" />
              Die Tradition des Schenkens zu Eid
            </h2>
            
            <p className="text-[#6B7280] leading-relaxed mb-6">
              Das Schenken zu Eid hat eine lange Tradition im Islam und ist ein Ausdruck von 
              Liebe, Wertschätzung und Gemeinschaft. Ob <strong>Eid ul Fitr</strong> nach dem 
              Fastenmonat Ramadan oder <strong>Eid ul Adha</strong>, das Opferfest – beide 
              Feiertage sind perfekte Gelegenheiten, um Freude zu teilen und Beziehungen zu stärken.
            </p>

            <p className="text-[#6B7280] leading-relaxed mb-6">
              Traditionell wurden zu Eid vor allem <strong>Süßigkeiten, Datteln und neue Kleidung</strong> 
              verschenkt. Diese Geschenke symbolisieren Wohlstand, Süße des Lebens und einen 
              Neuanfang. Heute verbinden viele Familien diese Traditionen mit modernen Geschenkideen.
            </p>

            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mt-12 mb-6 flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-[#D4C5A9]" />
              Traditionelle Eid Geschenke
            </h2>

            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'Premium Datteln', desc: 'Hochwertige Medjool-Datteln in eleganter Verpackung' },
                { title: 'Süßigkeiten-Körbe', desc: 'Traditionelle Baklava, Ma\'amoul und Halal-Süßigkeiten' },
                { title: 'Attar & Oud Öle', desc: 'Klassische orientalische Düfte ohne Alkohol' },
                { title: 'Gebetsketten (Tasbih)', desc: 'Handgefertigte Tasbih aus Holz oder Halbedelsteinen' },
                { title: 'Arabisches Tee-Set', desc: 'Traditionelle Gläser mit Minztee-Zubehör' },
                { title: 'Gebetsteppiche', desc: 'Hochwertige Sajjada in verschiedenen Designs' },
              ].map((item, i) => (
                <div key={i} className="p-4 bg-white rounded-xl shadow-sm">
                  <h3 className="font-medium text-[#2C332E] mb-1">{item.title}</h3>
                  <p className="text-sm text-[#6B7280]">{item.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mt-12 mb-6 flex items-center gap-3">
              <Gift className="w-6 h-6 text-[#D4C5A9]" />
              Moderne Eid Geschenkideen
            </h2>

            <p className="text-[#6B7280] leading-relaxed mb-6">
              Neben traditionellen Geschenken erfreuen sich heute auch moderne <strong>Eid Mubarak 
              Geschenke</strong> großer Beliebtheit. Diese verbinden praktischen Nutzen mit 
              besonderer Wertschätzung:
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'Luxus-Parfumsets', desc: 'Hochwertige Halal-Parfums in Geschenkverpackung' },
                { title: 'Home-Spa Geschenksets', desc: 'Wellness-Produkte für entspannte Feiertage' },
                { title: 'Islamische Bücher', desc: 'Kinderbücher, Quran-Übersetzungen oder Biografien' },
                { title: 'Elektronik & Gadgets', desc: 'Digitale Quran-Player oder Smartwatches' },
                { title: 'Schmuck', desc: 'Elegante Halsketten, Armbänder oder Uhren' },
                { title: 'Erlebnisgeschenke', desc: 'Restaurant-Gutscheine oder Familienausflüge' },
              ].map((item, i) => (
                <div key={i} className="p-4 bg-[#F5F5F0] rounded-xl">
                  <h3 className="font-medium text-[#2C332E] mb-1">{item.title}</h3>
                  <p className="text-sm text-[#6B7280]">{item.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mt-12 mb-6 flex items-center gap-3">
              <Heart className="w-6 h-6 text-[#D4C5A9]" />
              Tipps für das perfekte Eid Geschenk
            </h2>

            <ol className="space-y-4 text-[#6B7280] mb-8">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#4A5D4F] text-white text-sm flex items-center justify-center">1</span>
                <span><strong>Kenne den Empfänger:</strong> Überlege, was der Person wirklich Freude bereiten würde.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#4A5D4F] text-white text-sm flex items-center justify-center">2</span>
                <span><strong>Qualität vor Quantität:</strong> Ein gut ausgewähltes Geschenk ist wertvoller als viele beliebige.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#4A5D4F] text-white text-sm flex items-center justify-center">3</span>
                <span><strong>Persönliche Note:</strong> Eine handgeschriebene Karte macht jedes Geschenk besonders.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#4A5D4F] text-white text-sm flex items-center justify-center">4</span>
                <span><strong>Schöne Verpackung:</strong> Die Präsentation ist Teil des Geschenkerlebnisses.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#4A5D4F] text-white text-sm flex items-center justify-center">5</span>
                <span><strong>Rechtzeitig planen:</strong> Bestelle früh genug, damit alles pünktlich zu Eid ankommt.</span>
              </li>
            </ol>

            <div className="mt-12 p-6 bg-[#4A5D4F] rounded-2xl text-center">
              <p className="text-white mb-4">
                Finde jetzt das perfekte Eid Mubarak Geschenk für deine Liebsten!
              </p>
              <Button asChild className="bg-white text-[#4A5D4F] hover:bg-[#F5F5F0] rounded-full px-8 py-3">
                <Link to="/geschenke">
                  Alle Eid Geschenke entdecken
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </article>
    </main>
  );
}
